// frontend/src/pages/TornarSeParceiroPage/TornarSeParceiroPage.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Form, Input, Button, Card, Typography, Alert, Tabs, Select, message,
} from 'antd';
import { MailOutlined, LockOutlined, UserOutlined } from '@ant-design/icons';
import logo from '../../assets/images/logo_vertical.jpg';
import { getErrorMessage } from '../../utils/errorHandler';
import { getDadosFinanceirosAfiliado, salvarDadosFinanceirosAfiliado } from '../../services/authService';
import { GoogleLoginButton, hasGoogleOAuth } from '../../components/GoogleLoginButton/GoogleLoginButton';

const { Title, Text } = Typography;

const TIPO_CHAVE_OPTIONS = [
  { value: 'cpf', label: 'CPF' },
  { value: 'email', label: 'E-mail' },
  { value: 'telefone', label: 'Telefone' },
  { value: 'aleatoria', label: 'Chave aleatória' },
];

function TornarSeParceiroPage() {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [loadingDados, setLoadingDados] = useState(false);
  const [dadosExistentes, setDadosExistentes] = useState(null);
  const auth = useAuth();
  const navigate = useNavigate();
  const [formLogin] = Form.useForm();
  const [formCadastro] = Form.useForm();
  const [formDados] = Form.useForm();

  const handleGoogleSuccess = async (tokenResponse) => {
    setError('');
    setLoadingGoogle(true);
    try {
      await auth.loginWithGoogle(tokenResponse.access_token);
    } catch (err) {
      setError(getErrorMessage(err, 'Não foi possível entrar com o Google.'));
    } finally {
      setLoadingGoogle(false);
    }
  };

  const handleGoogleError = () => {
    setError('Login com Google cancelado ou falhou.');
    setLoadingGoogle(false);
  };

  const onFinishLogin = async (values) => {
    setError('');
    setLoading(true);
    try {
      await auth.login(values.email, values.password);
    } catch (err) {
      setError(getErrorMessage(err, 'Não foi possível entrar.'));
    } finally {
      setLoading(false);
    }
  };

  const onFinishCadastro = async (values) => {
    setError('');
    setLoading(true);
    try {
      await auth.register(values.email, values.password, values.nome_completo);
    } catch (err) {
      setError(getErrorMessage(err, 'Não foi possível cadastrar.'));
    } finally {
      setLoading(false);
    }
  };

  const onFinishDados = async (values) => {
    setLoadingDados(true);
    setError('');
    try {
      await salvarDadosFinanceirosAfiliado(values);
      message.success('Dados cadastrados! Você já pode acessar o painel do parceiro.');
      navigate('/configuracoes');
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao salvar dados financeiros.'));
    } finally {
      setLoadingDados(false);
    }
  };

  useEffect(() => {
    if (auth.isAuthenticated) {
      getDadosFinanceirosAfiliado()
        .then((dados) => {
          setDadosExistentes(dados);
          formDados.setFieldsValue(dados);
        })
        .catch(() => setDadosExistentes(null));
    }
  }, [auth.isAuthenticated, formDados]);

  if (auth.isAuthenticated) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: 'var(--color-background)', padding: 24 }}>
        <Card style={{ maxWidth: 480, width: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <img src={logo} alt="Logo" style={{ height: 80, marginBottom: 16 }} />
            <Title level={4}>Complete seus dados para receber comissões</Title>
            <Text type="secondary">Informe sua chave PIX para receber as comissões por indicação.</Text>
          </div>

          {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

          <Form form={formDados} layout="vertical" onFinish={onFinishDados}>
            <Form.Item
              name="tipo_chave"
              label="Tipo da chave PIX"
              rules={[{ required: true, message: 'Selecione o tipo' }]}
            >
              <Select placeholder="Selecione" options={TIPO_CHAVE_OPTIONS} />
            </Form.Item>
            <Form.Item
              name="chave_pix"
              label="Chave PIX"
              rules={[{ required: true, message: 'Informe a chave PIX' }]}
            >
              <Input placeholder="Ex: 123.456.789-00 ou seu@email.com" />
            </Form.Item>
            <Form.Item
              name="nome_completo_titular"
              label="Nome completo do titular"
              rules={[{ required: true, message: 'Obrigatório' }]}
            >
              <Input prefix={<UserOutlined />} placeholder="Como consta no banco" />
            </Form.Item>
            <Form.Item
              name="cpf_titular"
              label="CPF do titular"
              rules={[{ required: true, message: 'Obrigatório' }]}
            >
              <Input placeholder="000.000.000-00" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" block loading={loadingDados} size="large">
                {dadosExistentes ? 'Atualizar dados' : 'Cadastrar e continuar'}
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Link to="/configuracoes">Voltar às configurações</Link>
          </div>
        </Card>
      </div>
    );
  }

  const tabItems = [
    {
      key: 'entrar',
      label: 'Já tenho conta',
      children: (
        <Form form={formLogin} onFinish={onFinishLogin} layout="vertical" size="large">
          <Form.Item name="email" rules={[{ required: true }]}>
            <Input prefix={<MailOutlined />} placeholder="E-mail" />
          </Form.Item>
          <Form.Item name="password" rules={[{ required: true }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="Senha" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              Entrar
            </Button>
          </Form.Item>
        </Form>
      ),
    },
    {
      key: 'cadastrar',
      label: 'Criar conta',
      children: (
        <Form form={formCadastro} onFinish={onFinishCadastro} layout="vertical" size="large">
          <Form.Item name="nome_completo" rules={[{ required: true }]}>
            <Input prefix={<UserOutlined />} placeholder="Nome completo" />
          </Form.Item>
          <Form.Item name="email" rules={[{ required: true, type: 'email' }]}>
            <Input prefix={<MailOutlined />} placeholder="E-mail" />
          </Form.Item>
          <Form.Item name="password" rules={[{ required: true, min: 8 }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="Senha" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              Cadastrar
            </Button>
          </Form.Item>
        </Form>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: 'var(--color-background)', padding: 24 }}>
      <Card style={{ maxWidth: 420, width: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img src={logo} alt="Logo" style={{ height: 80, marginBottom: 16 }} />
          <Title level={4}>Tornar-se parceiro</Title>
          <Text type="secondary">Faça login ou crie uma conta para cadastrar seus dados e começar a indicar.</Text>
        </div>

        {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

        {hasGoogleOAuth && (
          <div style={{ marginBottom: 16 }}>
            <GoogleLoginButton
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              loading={loadingGoogle}
              disabled={loading}
              block
              size="large"
            />
          </div>
        )}

        <Tabs items={tabItems} centered />

        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Link to="/login">Já tenho conta – Ir para login</Link>
        </div>
      </Card>
    </div>
  );
}

export default TornarSeParceiroPage;
